import type * as PublicType from '@/types/publicType'

/**
 * 时间格式处理
 * @param {*} formatter 时间格式
 * @returns
 */
const _formatterNormalize = (formatter: string | (() => void)) => {
  if (typeof formatter === 'function') {
    return formatter
  }

  const formatterFunc = (dateInfo: PublicType.DateInfoProp) => {
    const { yyyy, MM, dd, HH, mm, ss, ms } = dateInfo
    return formatter
      .replace('yyyy', yyyy)
      .replace('MM', MM)
      .replace('dd', dd)
      .replace('HH', HH)
      .replace('mm', mm)
      .replace('ss', ss)
      .replace('ms', ms)
  }
  return formatterFunc
}

/**
 * 根据日期或时间戳获取年月日时分秒
 * @param {*} time 日期或时间戳
 * @param {*} formatter 需要展示的时间格式
 * @param {*} isPad 是否补0
 * @returns
 */
const formatterTime = (
  time: number | string,
  formatter: string | (() => void) = 'yyyy-MM-dd',
  isPad: boolean = true
) => {
  if (!time) return
  if (typeof time === 'string') {
    if (/^[0-9]+$/.test(time)) {
      // support "1548221490638"
      time = parseInt(time)
    } else {
      // support safari
      // https://stackoverflow.com/questions/4310953/invalid-date-in-safari
      time = time.replace(new RegExp(/-/gm), '/')
    }
  }
  if (typeof time === 'number' && time.toString().length === 10) {
    time = time * 1000
  }
  const date = new Date(time)
  const formatterNew = _formatterNormalize(formatter)
  const dateInfo: PublicType.DateInfoProp = {
    yyyy: date.getFullYear().toString(),
    MM: (date.getMonth() + 1).toString(),
    dd: date.getDate().toString(),
    HH: date.getHours().toString(),
    mm: date.getMinutes().toString(),
    ss: date.getSeconds().toString(),
    ms: date.getMilliseconds().toString()
  }
  const _pad = (prop: keyof PublicType.DateInfoProp, len: number) => {
    dateInfo[prop] = dateInfo[prop]?.padStart(len, '0')
  }
  if (isPad) {
    _pad('yyyy', 4)
    _pad('MM', 2)
    _pad('dd', 2)
    _pad('HH', 2)
    _pad('mm', 2)
    _pad('ss', 2)
    _pad('ms', 3)
  }
  const result = formatterNew(dateInfo)
  return result
}

/**
 * 生成一个永不重复的ID
 * @param { Number } randomLength
 */
const getUuiD = (randomLength?: number) => {
  return Number(
    Math.random().toString().substr(2, randomLength) + Date.now()
  ).toString(36)
}

const stringifyDate = function (datetime, simple = false) {
  const i18n = useNuxtApp().$i18n
  const weekMap = [
    i18n.t('public.Sunday'),
    i18n.t('public.Monday'),
    i18n.t('public.Tuesday'),
    i18n.t('public.Wednesday'),
    i18n.t('public.Thursday'),
    i18n.t('public.Friday'),
    i18n.t('public.Saturday')
  ]
  // let weekMap = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六']
  datetime = new Date(datetime)
  const year = datetime.getFullYear()
  const simpleYear = datetime.getYear() - 100
  let month = datetime.getMonth() + 1
  month = month > 9 ? month : '0' + month
  let day = datetime.getDate()
  day = day > 9 ? day : '0' + day
  let hour = datetime.getHours()
  hour = hour > 9 ? hour : '0' + hour
  let min = datetime.getMinutes()
  min = min > 9 ? min : '0' + min
  let week = datetime.getDay()
  week = weekMap[week]
  const thatDay = new Date(year, month - 1, day, 0, 0, 0).getTime()

  if (simple) {
    return {
      withYear: `${day}/${month}/${simpleYear}`,
      withMonth: `${month}-${day}`,
      withDay: `${week}`,
      withLastDay: `${i18n.t('public.Yesterday')}`,
      withHour: `${hour}:${min}`,
      thatDay
    }
  } else {
    return {
      withYear: `${year}-${month}-${day} ${hour}:${min}`,
      withMonth: `${month}-${day} ${hour}:${min}`,
      withDay: `${week} ${hour}:${min}`,
      withLastDay: `${i18n.t('public.Yesterday')} ${hour}:${min}`,
      withHour: `${hour}:${min}`,
      thatDay
    }
  }
}

/* 格式化聊天日期 */
const formatChatDate = function (datetime: number, simple = false) {
  const tempDate = new Date().getTime()
  const result = stringifyDate(datetime, simple)
  const thatDay = result.thatDay
  const deltaTime = (tempDate - thatDay) / 1000

  if (deltaTime < 3600 * 24) {
    return result.withHour
  } else if (deltaTime < 3600 * 24 * 2) {
    return result.withLastDay
  } else if (deltaTime < 3600 * 24 * 7) {
    return result.withDay
  } else if (deltaTime < 3600 * 24 * 30) {
    return result.withMonth
  } else {
    return result.withYear
  }
}

// 消息类型
const mapMsgType = function (msg) {
  const i18n = useNuxtApp().$i18n
  const map = {
    text: i18n.t('public.TM'), //'文本消息'
    image: i18n.t('public.PM'), //'图片消息'
    file: i18n.t('public.FM'), //'文件消息',
    audio: i18n.t('public.VM'), //'语音消息',
    video: i18n.t('public.VM2'), //'视频消息',
    geo: i18n.t('public.GM'), //'地理位置消息',
    tip: i18n.t('public.RM'), //'提醒消息',
    custom: i18n.t('public.CM'), //'自定义消息',
    notification: i18n.t('public.SN'), //'系统通知',
    robot: i18n.t('public.RM2') //'机器人消息'
  }
  const type = msg.type
  return map[type] || i18n.t('public.UM') //'未知消息类型'
}

// 解析scene 私聊还是群聊
const parseSession = function (sessionId: string) {
  if (/^p2p-/.test(sessionId)) {
    return {
      scene: 'p2p',
      to: sessionId.replace(/^p2p-/, '')
    }
  } else if (/^team-/.test(sessionId)) {
    return {
      scene: 'team',
      to: sessionId.replace(/^team-/, '')
    }
  }
}

//是否移动端
const isMobile = function () {
  const flag = navigator.userAgent.match(
    /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i
  )
  return flag
}

// Base64加密
const encryptParam = (param: string) => {
  const str = JSON.stringify(param)
  const Base64Str = window.btoa(str)
  return Base64Str
}

// Base64解密
const decryptParam = (Base64Str: string) => {
  const str = window.atob(Base64Str)
  const param = JSON.parse(str)
  return param
}

// 判断当前浏览器是否支持webp
const isLoadMinHeaderWebp = () => {
  if (process.client) {
    return new Promise((resolve) => {
      window['isSupportWebp'] = false //是否支持

      const img = new Image()
      function getResult(event: any) {
        //如果进入加载且图片宽度为1(通过图片宽度值判断图片是否可以显示)
        window['isSupportWebp'] =
          event && event.type === 'load' ? img.width >= 1 : false
        resolve(window['isSupportWebp'])
      }
      img.onerror = getResult
      img.onload = getResult
      img.src =
        'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=' //一像素图片
    })
  }
}
isLoadMinHeaderWebp()

/**
 * 七牛云图片处理
 * https://developer.qiniu.com/dora/1279/basic-processing-images-imageview2
 * @param {string | undefined} picUrl 图片链接
 * @param {0 | 1 | 2 | 3 | 4 | 5} mode 默认：1，定义等比缩放、设定目标宽高缩放的多种不同方式，取值范围：[0,5]的整数
 * @param {number} w 裁剪宽度比例
 * @param {number} h 裁剪高度比例
 */
export interface imageView2Interface {
  mode?: 0 | 1 | 2 | 3 | 4 | 5
  w?: number
  h?: number
  /** 是否用window.isSupportWebp判断支持webp */
  isSupportWebp?: boolean
  /** 是否添加webp */
  isWebp?: boolean
}
const handlePicUrl = (
  picUrl: string | undefined,
  { mode = 1, w, h, isSupportWebp = true, isWebp }: imageView2Interface = {}
): string => {
  if (!picUrl) return ''
  const runtimeConfig = useRuntimeConfig()
  if (
    picUrl.includes(runtimeConfig.public.qnUrl) ||
    picUrl.includes(runtimeConfig.public.qnUrl2) ||
    picUrl.includes(runtimeConfig.public.qnUrl3) ||
    picUrl.includes(runtimeConfig.public.qnUrl4) ||
    picUrl.includes(runtimeConfig.public.qnUrl5) ||
    picUrl.includes(runtimeConfig.public.qnUrl6)
  ) {
    // 七牛云
    let url = ''

    // 判断是否有图片瘦身前缀，有就直接复制url，没有就添加
    const imageslim = '?imageslim' // 片瘦身前缀
    if (picUrl.includes(imageslim)) {
      url = picUrl
    } else {
      url = picUrl + imageslim
    }

    // 判断缩放比例方式场景值
    if (mode || mode === 0) {
      const modeRegExp = /\|imageView2\/[0-5]/ // 正则表达式
      if (url.search(modeRegExp) !== -1) {
        url = url.replace(modeRegExp, `|imageView2/${mode}`)
      } else {
        url += `|imageView2/${mode}`
      }
    }

    // 添加裁剪比例宽
    if (w) {
      // 如果已有裁剪数值就移除重新添加
      const wRegExp = /\/w\/\d+/ // 正则表达式
      if (url.search(wRegExp) !== -1) {
        url = url.replace(wRegExp, `/w/${w}`)
      } else {
        url += `/w/${w}`
      }
    }
    // 添加裁剪比例高
    if (h) {
      // 如果已有裁剪数值就移除重新添加
      const hRegExp = /\/h\/\d+/ // 正则表达式
      if (url.search(hRegExp) !== -1) {
        url = url.replace(hRegExp, `/h/${h}`)
      } else {
        url += `/h/${h}`
      }
    }

    // 判断是否支持webp
    url = url.replaceAll('/format/webp', '') // 过滤一次保证不重复添加
    if (isSupportWebp) {
      if (window['isSupportWebp']) {
        url += '/format/webp'
      }
    }
    if (isWebp) {
      url = url.replaceAll('/format/webp', '') // 过滤一次保证不重复添加
      url += '/format/webp'
    }

    // 触发同步输出限制，返回原图
    url = url.replaceAll('/ignore-error/1', '') // 过滤一次保证不重复添加
    url += '/ignore-error/1'

    return url
  } else {
    return picUrl
  }
}

/**
 * 七牛云图片处理头像
 * @param {string | undefined} picUrl 图片链接
 */
const handlePicUrlAvatar = (
  picUrl: string | undefined,
  imageConfig: imageView2Interface = {}
): string => {
  return handlePicUrl(picUrl, {
    mode: 1,
    w: 140,
    h: 140,
    ...imageConfig
  })
}

/**
 * 七牛云图片处理国家
 * @param {string | undefined} picUrl 图片链接
 */
const handlePicUrlCountryIcon = (
  picUrl: string | undefined,
  imageConfig: imageView2Interface = {}
): string => {
  return handlePicUrl(picUrl, { mode: 2, w: 60, h: 40, ...imageConfig })
}

/**
 * 七牛云图片处理礼物
 * @param {string | undefined} picUrl 图片链接
 */
const handlePicUrlGift = (
  picUrl: string | undefined,
  imageConfig: imageView2Interface = {}
): string => {
  return handlePicUrl(picUrl, { mode: 1, w: 220, h: 220, ...imageConfig })
}

/**
 * 七牛云图片处理会话
 * @param {string | undefined} picUrl 图片链接
 */
const handlePicUrlChatItem = (
  picUrl: string | undefined,
  imageConfig: imageView2Interface = {}
): string => {
  return handlePicUrl(picUrl, { mode: 0, w: 300, ...imageConfig })
}

// 判断浏览器类型
const checkVersion = () => {
  const u = navigator.userAgent
  return {
    trident: u.indexOf('Trident') > -1, //IE内核
    presto: u.indexOf('Presto') > -1, //opera内核
    webKit: u.indexOf('AppleWebKit') > -1, //苹果、谷歌内核
    gecko: u.indexOf('Gecko') > -1 && u.indexOf('KHTML') == -1, //火狐内核
    mobile: !!u.match(/AppleWebKit.*Mobile.*/), //是否为移动终端
    ios:
      !!u.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) ||
      !!u.match(/\(i[^;]+;(U;)?CPU[^)]+MacOSX/), //ios终端
    android: u.indexOf('Android') > -1 || u.indexOf('Adr') > -1, //android终端
    iPhone: u.indexOf('iPhone') > -1, //是否为iPhone或者QQHD浏览器
    iPad: u.indexOf('iPad') > -1, //是否iPad
    webApp: u.indexOf('Safari') == -1, //是否web应该程序，没有头部与底部
    weixin: u.indexOf('MicroMessenger') > -1, //是否微信
    qq: !!u.match(/\sQQ/i), //是否QQ
    facebook: u.indexOf('FBAN') > -1 || u.indexOf('FBAV') > -1, // 是否facebook
    webView:
      /webview/i.test(u) ||
      /wv/.test(u) ||
      (/iPhone|iPad|iPod/i.test(u) &&
        /Safari/i.test(u) &&
        !/Chrome/i.test(u) &&
        !/CriOS/i.test(u)),
    safari: /Safari/.test(u) && !/Chrome/.test(u) && !/CriOS/.test(u), // 是否为 Safari 浏览器，在苹果手机谷歌浏览器判断Safari也会为true
    app:
      u.indexOf('leekalive') > -1 ||
      u.indexOf('Leekalive') > -1 ||
      u.indexOf('LeeSki') > -1 ||
      u.indexOf('leeSki') > -1 //是否在app内
  }
}

// 一维数组转二维数组
const arrayToMatrix = (arr, size) => {
  const result = [] as unknown[]
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size))
  }
  return result
}

// 处理毫秒 私聊页显示视频时长 时分秒
const formatOutput = (timeStamp: number) => {
  timeStamp = timeStamp / 1000
  const time = {
    d: '0',
    h: '00',
    m: '00',
    s: '00'
  }
  // 获取时分秒
  const h = parseInt((timeStamp / 3600).toString())
  const m = parseInt(((timeStamp - h * 3600) / 60).toString())
  const s = timeStamp - h * 3600 - m * 60
  // 格式化
  time.h = h < 10 ? `0${h}` : h.toString()
  time.m = m < 10 ? `0${m}` : m.toString()
  time.s = s < 10 ? `0${s}` : s.toString()
  return `${time.h}:${time.m}:${time.s}`
}

// 把url参数转换成对象格式
function getQueryVariable(variable) {
  //获取当前URL中的查询字符串部分（即问号后面的部分）
  if (process.client) {
    const query = window.location.search.substring(1)
    //把查询字符串按照&符号分割成数组
    const vars = query.split('&')
    //遍历数组中的每个元素
    for (let i = 0; i < vars.length; i++) {
      //把当前元素按照=符号分割成键和值
      const pair = vars[i].split('=')
      //如果当前元素的键等于参数名，则返回对应的值
      if (pair[0] == variable) {
        return pair[1]
      }
    }
    //如果没有找到匹配的参数名，则返回false
    return false
  }
}

// 获取语言
function getBrowserLanguage() {
  if (process.client) {
    return navigator.language || ''
  }
}

// 异步加载script
function loadScript(url: string, id: string, callback) {
  // 如果有此DOM就不再添加
  if (document.getElementById(id)) {
    callback()
    return
  }
  const script = document.createElement('script')
  script.id = id
  script.type = 'text/javascript'
  script.src = url

  // 脚本加载完成后执行回调函数
  script.onload = function () {
    callback()
  }

  document.head.appendChild(script)
}

/**
 * 格式化积分数值
 * @param {number | string} val 数值
 * @param {number} decimal 保留整数 默认：2
 * @param {Array<string>} units 单位数组 默认：['K', 'M', 'B']
 * @param {boolean} isFixed 是否固定保留decimal位小数 默认：false
 */
function valueToUnit(
  val?: number | string,
  decimal: number = 2,
  units: Array<string> = ['K', 'M', 'B'],
  isFixed: boolean = false
): string | number {
  if (!val) return 0
  val = Number(val)
  if (isNaN(val)) return val
  let index = -1
  while (val / 1000 >= 1 && index < units.length - 1) {
    val = val / 1000
    index++
  }
  val = Math.floor(val * Math.pow(10, decimal)) / Math.pow(10, decimal)
  if (isFixed) {
    val = Number(val).toFixed(decimal)
  }
  return val + (units[index] || '')
}

/**将base64转换为文件*/
export function dataURLtoFile(dataurl, filename) {
  const arr = dataurl.split(','),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

/**
 * 千分位添加逗号且保留两位小数
 * @param num
 */
export const addCommasToThousandths = (num: number) => {
  let result: string = String(num)
  if (num) {
    const decimal = num % 1
    num = Math.floor(num)
    result = num.toString().replace(/\d{1,3}(?=(\d{3})+(\.\d*)?$)/g, '$&,')
    result += decimal
      ? (Math.floor(decimal * 100) / 100).toString().slice(1)
      : ''
  }
  return result
}

/** 检测图片是否能正常请求 */
async function checkImageAvailability(imageUrl?: string) {
  if (!imageUrl) return Promise.resolve(false)
  return fetch(imageUrl, {
    method: 'HEAD' // 只请求头部信息
    // cache: 'no-cache' // 禁用缓存
  })
    .then((response) => {
      if (response.ok) {
        // 图片存在且可访问
        return true
      } else {
        // 图片无法访问，返回 false
        return false
      }
    })
    .catch(() => {
      // 网络错误或其他问题，返回 false
      return false
    })
}

export default {
  formatterTime,
  getUuiD,
  formatChatDate,
  mapMsgType,
  parseSession,
  isMobile,
  encryptParam,
  decryptParam,
  handlePicUrl,
  handlePicUrlAvatar,
  handlePicUrlCountryIcon,
  handlePicUrlGift,
  handlePicUrlChatItem,
  checkVersion,
  arrayToMatrix,
  formatOutput,
  getQueryVariable,
  loadScript,
  valueToUnit,
  getBrowserLanguage,
  dataURLtoFile,
  addCommasToThousandths,
  checkImageAvailability
}
